class ProductMediaGallery {
    constructor(container) {
        this.container = container;
        this.mainViewport = container.querySelector(".product-embla__viewport");
        this.prevButton = container.querySelector(
            ".product-embla__button--prev"
        );
        this.nextButton = container.querySelector(
            ".product-embla__button--next"
        );
        this.dotsContainer = container.querySelector(".product-embla__dots");
        this.emblaApi = null;
        this.dotNodes = [];

        // Device detection for responsive behavior
        this.isMobile = window.innerWidth < 768;
        this.isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
        this.isDesktop = window.innerWidth >= 1024;

        // Apply device-specific class to container
        this.updateDeviceClass();

        this.init();

        // Ensure container takes full height of parent on desktop only
        this.applyHeightStyles();

        // Handle resize events
        this.handleResize = this.handleResize.bind(this);
        window.addEventListener("resize", this.handleResize);
    }

    updateDeviceClass() {
        // Remove existing device classes
        this.container.classList.remove("is-mobile", "is-tablet", "is-desktop");

        // Add current device class
        if (this.isMobile) {
            this.container.classList.add("is-mobile");
        } else if (this.isTablet) {
            this.container.classList.add("is-tablet");
        } else {
            this.container.classList.add("is-desktop");
        }
    }

    applyHeightStyles() {
        // Update device detection
        this.isMobile = window.innerWidth < 768;
        this.isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
        this.isDesktop = window.innerWidth >= 1024;

        // Update device class
        this.updateDeviceClass();

        if (this.isDesktop) {
            // Desktop: Set full height
            this.container.style.height = "100%";
            if (this.container.parentElement) {
                this.container.parentElement.style.height = "100%";
            }

            // Ensure the product-embla elements also have full height
            const productEmbla = this.container.querySelector(".product-embla");
            if (productEmbla) {
                productEmbla.style.height = "100%";
            }
        } else if (this.isTablet) {
            // Tablet: Set specific height handling
            this.container.style.height = "100%";
            if (this.container.parentElement) {
                this.container.parentElement.style.height = "100%";
            }

            const productEmbla = this.container.querySelector(".product-embla");
            if (productEmbla) {
                productEmbla.style.height = "100%";
            }
        } else {
            // Mobile: Remove height constraints
            this.setupMobileView();
        }
    }

    setupMobileView() {
        // Clear all height-related inline styles for mobile
        this.container.style.height = "";
        this.container.style.maxHeight = ""; // Clear max-height explicitly
        this.container.style.maxWidth = ""; // Clear max-width explicitly
        if (this.container.parentElement) {
            this.container.parentElement.style.height = "";
            this.container.parentElement.style.maxHeight = ""; // Clear max-height explicitly
            this.container.parentElement.style.maxWidth = ""; // Clear max-width explicitly
        }

        const productEmbla = this.container.querySelector(".product-embla");
        if (productEmbla) {
            productEmbla.style.height = "";
            productEmbla.style.maxHeight = ""; // Clear max-height explicitly
            productEmbla.style.maxWidth = ""; // Clear max-width explicitly
        }

        // Also clear height on viewport and container
        if (this.mainViewport) {
            this.mainViewport.style.height = "";
            this.mainViewport.style.maxHeight = ""; // Clear max-height explicitly
            this.mainViewport.style.maxWidth = ""; // Clear max-width explicitly
        }

        const emblaContainer = this.container.querySelector(
            ".product-embla__container"
        );
        if (emblaContainer) {
            emblaContainer.style.height = "";
            emblaContainer.style.maxHeight = ""; // Clear max-height explicitly
            emblaContainer.style.maxWidth = ""; // Clear max-width explicitly
        }

        // Clear any fixed heights on slides
        const slides = this.container.querySelectorAll(".product-embla__slide");
        slides.forEach((slide) => {
            slide.style.height = "";
            slide.style.maxHeight = ""; // Clear max-height explicitly
            slide.style.maxWidth = ""; // Clear max-width explicitly
        });

        // Clear any fixed heights on media elements
        const mediaItems = this.container.querySelectorAll(".product__media");
        mediaItems.forEach((item) => {
            item.style.height = "";
            item.style.maxHeight = ""; // Clear max-height explicitly
            item.style.maxWidth = ""; // Clear max-width explicitly
        });

        const parentSection = document.querySelectorAll(".product-details");
        parentSection.forEach((item) => {
            item.style.height = "";
            item.style.maxHeight = ""; // Clear max-height explicitly
            item.style.maxWidth = ""; // Clear max-width explicitly
        });

        const infoSection = document.querySelectorAll(
            ".product-details__info-column"
        );
        infoSection.forEach((item) => {
            item.style.height = "";
            item.style.maxHeight = ""; // Clear max-height explicitly
            item.style.maxWidth = ""; // Clear max-width explicitly
        });
    }

    handleResize() {
        // Check if device type has changed
        const wasMobile = this.isMobile;
        const wasTablet = this.isTablet;
        const wasDesktop = this.isDesktop;

        // Update device detection
        this.isMobile = window.innerWidth < 768;
        this.isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
        this.isDesktop = window.innerWidth >= 1024;

        // If going from desktop/tablet to mobile, always clear height styles
        if (!wasMobile && this.isMobile) {
            this.setupMobileView();
        }

        // Only apply other changes if device type has changed
        if (
            wasMobile !== this.isMobile ||
            wasTablet !== this.isTablet ||
            wasDesktop !== this.isDesktop
        ) {
            // Apply appropriate height styles based on screen width
            this.applyHeightStyles();

            // Update device class
            this.updateDeviceClass();

            // Ensure dots are properly positioned
            this.ensureProperDotPosition();

            // Re-initialize the carousel to adjust for new dimensions
            if (this.emblaApi) {
                this.emblaApi.reInit();
            }
        }
    }

    init() {
        // Ensure embla carousel manager is loaded
        if (!window.emblaCarouselManager) {
            console.error("Embla Carousel Manager not found");
            return;
        }

        window.emblaCarouselManager.loadEmblaScript().then(() => {
            this.setupMainCarousel();
            this.setupDotNavigation();
            this.setupEvents();
            this.setupActiveMedia();
            this.ensureProperDotPosition();
        });
    }

    setupMainCarousel() {
        const options = {
            loop: true,
            draggable: true,
            skipSnaps: false,
            containScroll: "trimSnaps",
            watchDrag: true,
        };

        this.emblaApi = window.emblaCarouselManager.initCarousel(
            `product-embla-${this.container.dataset.sectionId || "main"}`,
            this.mainViewport,
            options
        );

        // Setup arrow navigation if available
        if (this.prevButton && this.nextButton) {
            this.prevButton.addEventListener("click", () =>
                this.emblaApi.scrollPrev()
            );
            this.nextButton.addEventListener("click", () =>
                this.emblaApi.scrollNext()
            );

            // Handle button disabled state
            const updateButtonsState = () => {
                if (this.emblaApi.canScrollPrev()) {
                    this.prevButton.removeAttribute("disabled");
                } else {
                    this.prevButton.setAttribute("disabled", "true");
                }

                if (this.emblaApi.canScrollNext()) {
                    this.nextButton.removeAttribute("disabled");
                } else {
                    this.nextButton.setAttribute("disabled", "true");
                }
            };

            this.emblaApi.on("select", updateButtonsState);
            this.emblaApi.on("init", updateButtonsState);
            this.emblaApi.on("reInit", updateButtonsState);
        }
    }

    setupDotNavigation() {
        if (!this.dotsContainer) return;

        // Clear any existing dots
        this.dotsContainer.innerHTML = "";
        this.dotNodes = [];

        // Create dot buttons for each slide
        const slides = this.emblaApi.slideNodes();
        slides.forEach((_, index) => {
            const dot = document.createElement("button");
            dot.className = "product-embla__dot";
            dot.setAttribute("aria-label", `Go to slide ${index + 1}`);

            // Add click event to scroll to slide
            dot.addEventListener("click", () => this.emblaApi.scrollTo(index));

            this.dotsContainer.appendChild(dot);
            this.dotNodes.push(dot);
        });

        // Set initial selected dot
        this.updateDots();

        // Update dots when slide changes
        this.emblaApi.on("select", this.updateDots.bind(this));
        this.emblaApi.on("reInit", this.updateDots.bind(this));
    }

    updateDots() {
        const selectedIndex = this.emblaApi.selectedScrollSnap();
        this.dotNodes.forEach((dot, index) => {
            dot.classList.toggle(
                "product-embla__dot--selected",
                index === selectedIndex
            );
        });
    }

    setupEvents() {
        // Handle 3D model button clicks
        this.container
            .querySelectorAll("[data-shopify-xr]")
            .forEach((button) => {
                button.addEventListener(
                    "click",
                    this.handleXrButton.bind(this)
                );
            });

        // Handle modal triggers
        this.container
            .querySelectorAll(".product__modal-opener")
            .forEach((opener) => {
                opener.addEventListener(
                    "click",
                    this.handleModalTrigger.bind(this)
                );
            });
    }

    handleXrButton(evt) {
        // Pass the XR button click to Shopify's XR module
        document.dispatchEvent(
            new CustomEvent("shopify_xr_launch", {
                detail: {
                    model_id: evt.currentTarget.dataset.shopifyModel3dId,
                },
            })
        );
    }

    handleModalTrigger(evt) {
        // Find the target modal ID
        const modalId = evt.currentTarget.dataset.modal;
        if (!modalId) return;

        const modal = document.querySelector(modalId);
        if (modal) {
            // Find the current slide index
            const index = this.emblaApi.selectedScrollSnap();
            const currentSlide = this.emblaApi.slideNodes()[index];
            const mediaId = currentSlide.dataset.mediaId;

            // Trigger modal open with the current media
            modal.show(mediaId);
        }
    }

    setupActiveMedia() {
        // Initialize with the first slide
        const initialIndex = this.emblaApi.selectedScrollSnap();
        this.updateActiveMedia(initialIndex);
    }

    updateActiveMedia(index) {
        const slides = this.emblaApi.slideNodes();
        const currentSlide = slides[index];

        if (!currentSlide) return;

        // Update aria-current
        slides.forEach((slide) => {
            slide.setAttribute(
                "aria-current",
                slide === currentSlide ? "true" : "false"
            );
        });

        // Handle media loading if needed (for videos, 3D models)
        const mediaId = currentSlide.dataset.mediaId;
        if (mediaId) {
            // Update the gallery status for screen readers
            const statusElement =
                this.container.querySelector('[role="status"]');
            if (statusElement) {
                const mediaType = currentSlide.dataset.mediaType || "image";
                const mediaPosition = currentSlide.dataset.mediaPosition || "1";
                const mediaCount = slides.length;

                statusElement.textContent = `${mediaType} ${mediaPosition} of ${mediaCount}`;
            }

            // Expose a method to allow external code to set active media
            this.container.setActiveMedia = (mediaId, scroll = true) => {
                const mediaIndex = Array.from(slides).findIndex(
                    (slide) => slide.dataset.mediaId === mediaId
                );
                if (mediaIndex >= 0) {
                    if (scroll) {
                        this.emblaApi.scrollTo(mediaIndex);
                    } else {
                        this.updateActiveMedia(mediaIndex);
                    }
                }
            };
        }
    }

    ensureProperDotPosition() {
        // Make sure the dots are overlaid on the image regardless of device
        if (this.dotsContainer) {
            this.dotsContainer.style.position = "absolute";
            this.dotsContainer.style.bottom = "20px";
            this.dotsContainer.style.padding = "0";
            this.dotsContainer.style.zIndex = "10";
        }
    }

    // Add a destroy method to clean up resources
    destroy() {
        // Remove event listeners
        window.removeEventListener("resize", this.handleResize);

        // Clean up Embla API
        if (this.emblaApi) {
            this.emblaApi.destroy();
            this.emblaApi = null;
        }

        // Remove device-specific classes
        this.container.classList.remove("is-mobile", "is-tablet", "is-desktop");

        // Remove any inline styles we've set
        this.container.style.height = "";
        this.container.style.maxHeight = ""; // Clear max-height explicitly
        this.container.style.maxWidth = ""; // Clear max-width explicitly
        if (this.container.parentElement) {
            this.container.parentElement.style.height = "";
            this.container.parentElement.style.maxHeight = ""; // Clear max-height explicitly
            this.container.parentElement.style.maxWidth = ""; // Clear max-width explicitly
        }

        const productEmbla = this.container.querySelector(".product-embla");
        if (productEmbla) {
            productEmbla.style.height = "";
            productEmbla.style.maxHeight = ""; // Clear max-height explicitly
            productEmbla.style.maxWidth = ""; // Clear max-width explicitly
        }

        if (this.mainViewport) {
            this.mainViewport.style.height = "";
            this.mainViewport.style.maxHeight = ""; // Clear max-height explicitly
            this.mainViewport.style.maxWidth = ""; // Clear max-width explicitly
        }

        const emblaContainer = this.container.querySelector(
            ".product-embla__container"
        );
        if (emblaContainer) {
            emblaContainer.style.height = "";
            emblaContainer.style.maxHeight = ""; // Clear max-height explicitly
            emblaContainer.style.maxWidth = ""; // Clear max-width explicitly
        }

        // Clear any fixed heights on slides
        const slides = this.container.querySelectorAll(".product-embla__slide");
        slides.forEach((slide) => {
            slide.style.height = "";
            slide.style.maxHeight = ""; // Clear max-height explicitly
            slide.style.maxWidth = ""; // Clear max-width explicitly
        });

        // Clear any fixed heights on media elements
        const mediaItems = this.container.querySelectorAll(".product__media");
        mediaItems.forEach((item) => {
            item.style.height = "";
            item.style.maxHeight = ""; // Clear max-height explicitly
            item.style.maxWidth = ""; // Clear max-width explicitly
        });

        const parentSection = document.querySelectorAll(".product-details");
        parentSection.forEach((item) => {
            item.style.height = "";
            item.style.maxHeight = ""; // Clear max-height explicitly
            item.style.maxWidth = ""; // Clear max-width explicitly
        });

        const infoSection = document.querySelectorAll(
            ".product-details__info-column"
        );
        infoSection.forEach((item) => {
            item.style.height = "";
            item.style.maxHeight = ""; // Clear max-height explicitly
            item.style.maxWidth = ""; // Clear max-width explicitly
        });
    }
}

// Store references to instances for cleanup
const galleryInstances = new Map();

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
    const mediaGalleries = document.querySelectorAll("media-gallery");
    mediaGalleries.forEach((gallery) => {
        const instance = new ProductMediaGallery(gallery);
        // Store reference with unique ID for later cleanup
        const id =
            gallery.id ||
            gallery.dataset.sectionId ||
            Math.random().toString(36).substring(2, 9);
        galleryInstances.set(id, instance);
    });
});

// Track when sections are loaded/reloaded in Shopify theme editor
document.addEventListener("shopify:section:load", (event) => {
    const mediaGallery = event.target.querySelector("media-gallery");
    if (mediaGallery) {
        const id =
            mediaGallery.id ||
            mediaGallery.dataset.sectionId ||
            Math.random().toString(36).substring(2, 9);
        // Clean up previous instance if it exists
        if (galleryInstances.has(id)) {
            galleryInstances.get(id).destroy();
        }
        // Create new instance
        const instance = new ProductMediaGallery(mediaGallery);
        galleryInstances.set(id, instance);
    }
});

// Clean up when sections are unloaded in theme editor
document.addEventListener("shopify:section:unload", (event) => {
    const mediaGallery = event.target.querySelector("media-gallery");
    if (mediaGallery) {
        const id =
            mediaGallery.id ||
            mediaGallery.dataset.sectionId ||
            Math.random().toString(36).substring(2, 9);
        if (galleryInstances.has(id)) {
            galleryInstances.get(id).destroy();
            galleryInstances.delete(id);
        }
    }
});
